// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    Checkbox,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem
} from '@mui/material';
import { ManageAccounts, Cancel } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import { deepPurple, grey } from '@mui/material/colors';
import useFetchData from 'hooks/useFetchData';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert, warningAlert } from 'ui-component/Toasts';

import { useTranslation } from "react-i18next";

const thirdPartyColumns = [
    { id: 'id', label: 'Count', minWidth: 30 },
    { id: 'name', label: 'Third Party Name', minWidth: 170 },
    { id: 'charges', label: 'Charges', minWidth: 170 },
];

const FiatCurrencyTab = () => {
    const { t } = useTranslation();

    const token = useSelector(selectCurrentToken);

    const [rateInputs, setRateInputs] = useState({});
    const [depositChargesInputs, setDepositChargesInputs] = useState({});
    const [withdrawalChargesInputs, setWithdrawalChargesInputs] = useState({});
    const [buyRateInputs, setBuyRateInputs] = useState({});

    const [rate, setRate] = useState(0);
    const [depositCharges, setDepositCharges] = useState(0);
    const [withdrawalCharges, setWithdrawalCharges] = useState(0);

    const handleRateChange = (id, value) => {
        setRateInputs(prev => ({ ...prev, [id]: value }));
    };

    const handleDepositChargesChange = (id, value) => {
        setDepositChargesInputs(prev => ({ ...prev, [id]: value }));
    };

    const handleWithdrawalChargesChange = (id, value) => {
        setWithdrawalChargesInputs(prev => ({ ...prev, [id]: value }));
    };

    const handleBuyRateChange = (id, value) => {
        setBuyRateInputs(prev => ({ ...prev, [id]: value }));
    };

    const [programDetails, setProgramDetails] = useState([]);
    const fetchData = async () => {
        try {
            const res = await axiosInstance.get(`/Backend/Currency/Fiat/All`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (res.status === 200) {
                setProgramDetails(res.data);
            }
        } catch (error) {

        }
    };

    useEffect(() => {
        if (programDetails.length > 0) {
            const newRateInputs = {};
            programDetails.forEach(detail => {
                newRateInputs[detail.id] = detail.rateFromUSDT;
            });
            setRateInputs(newRateInputs);

            const newDepositChargesInputs = {};
            programDetails.forEach(detail => {
                newDepositChargesInputs[detail.id] = detail.depositChargesPercentage;
            });
            setDepositChargesInputs(newDepositChargesInputs);

            const newWithdrawalChargesInputs = {};
            programDetails.forEach(detail => {
                newWithdrawalChargesInputs[detail.id] = detail.withdrawalChargesPercentage;
            });
            setWithdrawalChargesInputs(newWithdrawalChargesInputs);

            const newBuyRateInputs = {};
            programDetails.forEach(detail => {
                newBuyRateInputs[detail.id] = detail.rateToUSDT;
            });
            setBuyRateInputs(newBuyRateInputs);
        }
    }, [programDetails]);

    const [updateButtonDisabled, setUpdateButtonDisabled] = useState(false);
    const submitUpdateCurrency = async (id, newRate, newDepositCharges, newWithdrawalCharges, newBuyRate) => {
        setUpdateButtonDisabled(true);

        try {
            const res = await axiosInstance.put(
                '/Currency',
                {
                    id: id,
                    rateFromUSDT: newRate,
                    depositChargesPercentage: newDepositCharges,
                    withdrawalChargesPercentage: newWithdrawalCharges,
                    rateToUSDT: newBuyRate,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            if (res.status === 200) {
                successAlert('成功更新');
                fetchData();
            } else {
                errorAlert('发生错误');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setUpdateButtonDisabled(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: '20px' }}>
                <TableContainer sx={{ height: '80vh' }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key='details' style={{ width: 170 }}>{t("masterData.fiatCurrency")}</TableCell>
                                <TableCell key='rateFromUSDT' style={{ width: 170 }}>{t("masterData.rateFromUSDT")}</TableCell>
                                <TableCell key='rateToUSDT' style={{ width: 170 }}>{t("masterData.rateToUSDT")}</TableCell>
                                <TableCell key='depositChargesPercentage' style={{ width: 250 }}>{t("masterData.depositChargesPercentage")}</TableCell>
                                <TableCell key='withdrawalChargesPercentage' style={{ width: 250 }}>{t("masterData.withdrawalChargesPercentage")}</TableCell>
                                <TableCell key='button' style={{ width: 250 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {programDetails.map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                        <TableCell key='details' style={{fontSize: "12px"}}>
                                            {row.name}
                                        </TableCell>
                                        <TableCell key='rateFromUSDT' style={{fontSize: "12px"}}>
                                            <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                                <TextField 
                                                    type="number"
                                                    id="outlined-basic" 
                                                    variant="outlined" 
                                                    value={rateInputs[row.id] || ''}
                                                    onChange={(e) => handleRateChange(row.id, e.target.value)}
                                                    style={{fontSize: '12px'}}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell key='rateToUSDT' style={{fontSize: "12px"}}>
                                            <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                                <TextField 
                                                    type="number"
                                                    id="outlined-basic" 
                                                    variant="outlined" 
                                                    value={buyRateInputs[row.id] || ''}
                                                    onChange={(e) => handleBuyRateChange(row.id, e.target.value)}
                                                    style={{fontSize: '12px'}}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell key='depositChargesPercentage' style={{fontSize: "12px"}}>
                                            <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                                <TextField 
                                                    type="number"
                                                    id="outlined-basic" 
                                                    variant="outlined" 
                                                    value={depositChargesInputs[row.id] || ''}
                                                    onChange={(e) => handleDepositChargesChange(row.id, e.target.value)}
                                                    style={{fontSize: '12px'}}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell key='withdrawalChargesPercentage' style={{fontSize: "12px"}}>
                                            <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                                <TextField 
                                                    type="number"
                                                    id="outlined-basic" 
                                                    variant="outlined" 
                                                    value={withdrawalChargesInputs[row.id] || ''}
                                                    onChange={(e) => handleWithdrawalChargesChange(row.id, e.target.value)}
                                                    style={{fontSize: '12px'}}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell key='button' style={{fontSize: "12px"}}>
                                            <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                                <Button
                                                    variant="contained"
                                                    sx={{
                                                        display: 'block',
                                                        mx: 'auto',
                                                        width: '150px',
                                                        backgroundColor: deepPurple[500]
                                                    }}
                                                    onClick={() => submitUpdateCurrency(row.id, rateInputs[row.id], depositChargesInputs[row.id], withdrawalChargesInputs[row.id], buyRateInputs[row.id])}
                                                    disabled={updateButtonDisabled}
                                                >
                                                    {t("masterData.update")}
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </>
    );
};

export default FiatCurrencyTab;